import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  Stack,
  Stepper,
  Step,
  StepLabel,
  FormControlLabel,
  Checkbox,
  FormControl,
  FormLabel,
  Grid,
  Radio,
  RadioGroup
} from '@mui/material';
import {
  Storage as StorageIcon,
  Add as AddIcon,
  Close as CloseIcon,
  NavigateNext as NextIcon,
  NavigateBefore as BackIcon
} from '@mui/icons-material';
import { arquivarMonografia } from '../../services/arquivoDigitalService';

const ArquivarMonografia = ({ monografia, onSuccess }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Estado para controlar a etapa atual
  const [etapaAtual, setEtapaAtual] = useState(0);
  const totalEtapas = 3;
  
  // Estado para palavras-chave
  const [palavrasChave, setPalavrasChave] = useState([]);
  const [novaPalavraChave, setNovaPalavraChave] = useState('');
  
  // Estados para os novos campos
  const [dadosAutor, setDadosAutor] = useState({
    nomeAutor: '',
    biAutor: '',
    telefoneAutor: '',
    emailAutor: ''
  });

  const [dadosAcademicos, setDadosAcademicos] = useState({
    supervisor: '',
    coSupervisor: '',
    dataDefesa: new Date().toISOString().split('T')[0],
    areaConhecimento: '',
    programa: '',
    tipoTitulacao: 'Mestrado',
    tipoMaterial: 'Dissertação'
  });

  const [aceitesLegais, setAceitesLegais] = useState({
    aceiteDireitosAutorais: false,
    aceiteLicencaDistribuicao: false
  });
  
  // Preencher dados do autor a partir da monografia
  useEffect(() => {
    if (monografia?.projeto?.proposta?.estudante) {
      setDadosAutor(prev => ({
        ...prev,
        nomeAutor: monografia.projeto.proposta.estudante.nome || '',
        emailAutor: monografia.projeto.proposta.estudante.email || ''
      }));
    }
    
    if (monografia?.projeto?.supervisor?.Usuario) {
      setDadosAcademicos(prev => ({
        ...prev,
        supervisor: monografia.projeto.supervisor.Usuario.nome || ''
      }));
    }
  }, [monografia]);

  const handleOpen = () => {
    setDialogOpen(true);
    setError(null);
    setSuccess(false);
    setEtapaAtual(0);
  };

  const handleClose = () => {
    setDialogOpen(false);
    if (success && onSuccess) {
      onSuccess();
    }
  };

  const handleAdicionarPalavraChave = () => {
    if (novaPalavraChave.trim() !== '' && !palavrasChave.includes(novaPalavraChave.trim())) {
      setPalavrasChave([...palavrasChave, novaPalavraChave.trim()]);
      setNovaPalavraChave('');
    }
  };

  const handleRemoverPalavraChave = (index) => {
    const novasPalavrasChave = [...palavrasChave];
    novasPalavrasChave.splice(index, 1);
    setPalavrasChave(novasPalavrasChave);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdicionarPalavraChave();
    }
  };
  
  // Manipuladores para os novos campos
  const handleChangeDadosAutor = (e) => {
    const { name, value } = e.target;
    setDadosAutor({
      ...dadosAutor,
      [name]: value
    });
  };
  
  const handleChangeDadosAcademicos = (e) => {
    const { name, value } = e.target;
    setDadosAcademicos({
      ...dadosAcademicos,
      [name]: value
    });
  };
  
  const handleChangeAceitesLegais = (e) => {
    const { name, checked } = e.target;
    setAceitesLegais({
      ...aceitesLegais,
      [name]: checked
    });
  };
  
  // Navegar entre etapas
  const avancarEtapa = () => {
    if (etapaAtual < totalEtapas - 1) {
      setEtapaAtual(etapaAtual + 1);
    }
  };
  
  const voltarEtapa = () => {
    if (etapaAtual > 0) {
      setEtapaAtual(etapaAtual - 1);
    }
  };
  
  // Validações para habilitar o botão "Avançar"
  const validarEtapa1 = () => {
    // Requer pelo menos uma palavra-chave
    return true; // Opcional, já que vamos gerar palavras-chave automaticamente
  };
  
  const validarEtapa2 = () => {
    return dadosAutor.nomeAutor.trim() !== ''; // Nome é obrigatório
  };
  
  const validarEtapa3 = () => {
    return aceitesLegais.aceiteDireitosAutorais && aceitesLegais.aceiteLicencaDistribuicao;
  };
  
  const etapaValida = () => {
    switch (etapaAtual) {
      case 0: return validarEtapa1();
      case 1: return validarEtapa2();
      case 2: return validarEtapa3();
      default: return false;
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Construir o payload com todos os dados
      const payload = {
        monografiaId: monografia.id,
        palavrasChave,
        ...dadosAutor,
        ...dadosAcademicos,
        ...aceitesLegais
      };
      
      console.log('Enviando dados:', payload);
      
      const response = await arquivarMonografia(payload);
      
      console.log('Monografia arquivada com sucesso:', response);
      console.log('ID do arquivo digital criado:', response.arquivo?.id);
      console.log('Resposta completa:', JSON.stringify(response));
      
      setSuccess(true);
      
    } catch (err) {
      console.error('Erro ao arquivar monografia:', err);
      setError(err.response?.data?.message || 'Erro ao arquivar monografia no repositório');
    } finally {
      setLoading(false);
    }
  };
  
  // Renderização do conteúdo de cada etapa
  const renderEtapa1 = () => (
    <Box>
      <Typography variant="h6" gutterBottom>Informações Básicas e Palavras-chave</Typography>
      
      <Paper sx={{ p: 3, mb: 3 }} variant="outlined">
        <Typography variant="subtitle1" gutterBottom>
          Informações da Monografia
        </Typography>
        <Typography variant="h6" color="primary" gutterBottom>
          {monografia.projeto?.proposta?.titulo || `Monografia #${monografia.id}`}
        </Typography>
        <Typography variant="body2" gutterBottom>
          <strong>ID:</strong> {monografia.id}
        </Typography>
        <Typography variant="body2" gutterBottom>
          <strong>Estudante:</strong> {monografia.projeto?.proposta?.estudante?.nome || 'N/A'}
        </Typography>
        <Typography variant="body2" gutterBottom>
          <strong>Versão:</strong> {monografia.versao_documento}
        </Typography>
        <Typography variant="body2">
          <strong>Data de Submissão:</strong> {new Date(monografia.data_submissao).toLocaleDateString()}
        </Typography>
      </Paper>
      
      <Typography variant="subtitle1" gutterBottom>
        Palavras-chave
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Adicione palavras-chave para facilitar a busca desta monografia no repositório.
        Se não adicionar, o sistema tentará extrair automaticamente.
      </Typography>
      
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <TextField
          label="Nova palavra-chave"
          value={novaPalavraChave}
          onChange={(e) => setNovaPalavraChave(e.target.value)}
          onKeyPress={handleKeyPress}
          size="small"
          fullWidth
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleAdicionarPalavraChave}
          startIcon={<AddIcon />}
        >
          Adicionar
        </Button>
      </Box>
      
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 3 }}>
        {palavrasChave.map((palavra, index) => (
          <Chip
            key={index}
            label={palavra}
            onDelete={() => handleRemoverPalavraChave(index)}
            deleteIcon={<CloseIcon />}
          />
        ))}
        {palavrasChave.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Nenhuma palavra-chave adicionada
          </Typography>
        )}
      </Stack>
    </Box>
  );
  
  const renderEtapa2 = () => (
    <Box>
      <Typography variant="h6" gutterBottom>Dados do Autor e Informações Acadêmicas</Typography>
      
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>Identificação do Autor</Typography>
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            label="Nome Completo"
            name="nomeAutor"
            value={dadosAutor.nomeAutor}
            onChange={handleChangeDadosAutor}
            fullWidth
            required
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            label="BI/Documento de Identidade"
            name="biAutor"
            value={dadosAutor.biAutor}
            onChange={handleChangeDadosAutor}
            fullWidth
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            label="Telefone"
            name="telefoneAutor"
            value={dadosAutor.telefoneAutor}
            onChange={handleChangeDadosAutor}
            fullWidth
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            label="E-mail"
            name="emailAutor"
            value={dadosAutor.emailAutor}
            onChange={handleChangeDadosAutor}
            fullWidth
            type="email"
          />
        </Grid>
      </Grid>
      
      <Divider sx={{ my: 3 }} />
      
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>Informações Acadêmicas</Typography>
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            label="Supervisor"
            name="supervisor"
            value={dadosAcademicos.supervisor}
            onChange={handleChangeDadosAcademicos}
            fullWidth
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            label="Co-Supervisor"
            name="coSupervisor"
            value={dadosAcademicos.coSupervisor}
            onChange={handleChangeDadosAcademicos}
            fullWidth
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            label="Data de Defesa"
            name="dataDefesa"
            type="date"
            value={dadosAcademicos.dataDefesa}
            onChange={handleChangeDadosAcademicos}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            label="Área do Conhecimento"
            name="areaConhecimento"
            value={dadosAcademicos.areaConhecimento}
            onChange={handleChangeDadosAcademicos}
            fullWidth
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            label="Programa/Curso"
            name="programa"
            value={dadosAcademicos.programa}
            onChange={handleChangeDadosAcademicos}
            fullWidth
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <FormLabel>Titulação</FormLabel>
            <RadioGroup
              row
              name="tipoTitulacao"
              value={dadosAcademicos.tipoTitulacao}
              onChange={handleChangeDadosAcademicos}
            >
               <FormControlLabel value="Licenciatura" control={<Radio />} label="Licenciatura" />
              <FormControlLabel value="Mestrado" control={<Radio />} label="Mestrado" />
              <FormControlLabel value="Doutoramento" control={<Radio />} label="Doutoramento" />
            </RadioGroup>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <FormLabel>Tipo de Material</FormLabel>
            <RadioGroup
              row
              name="tipoMaterial"
              value={dadosAcademicos.tipoMaterial}
              onChange={handleChangeDadosAcademicos}
            >
              <FormControlLabel value="Monografia" control={<Radio />} label="Monografia" />
              <FormControlLabel value="Dissertação" control={<Radio />} label="Dissertação" />
              <FormControlLabel value="Tese" control={<Radio />} label="Tese" />
            </RadioGroup>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );
  
  const renderEtapa3 = () => (
    <Box>
      <Typography variant="h6" gutterBottom>Direitos de Propriedade Intelectual</Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="subtitle2">Importante</Typography>
        <Typography variant="body2">
          Antes de arquivar sua monografia no repositório digital, é necessário aceitar os termos abaixo.
        </Typography>
      </Alert>
      
      <Paper sx={{ p: 3, mb: 3 }} variant="outlined">
        <FormControlLabel
          control={
            <Checkbox
              checked={aceitesLegais.aceiteDireitosAutorais}
              onChange={handleChangeAceitesLegais}
              name="aceiteDireitosAutorais"
              color="primary"
            />
          }
          label={
            <Typography variant="body2">
              <strong>Declaração de Direitos Autorais:</strong> Retenho todos os direitos de autor relativos à tese ou dissertação, e o direito de a usar em trabalhos futuros.
            </Typography>
          }
        />
      </Paper>
      
      <Paper sx={{ p: 3, mb: 3 }} variant="outlined">
        <FormControlLabel
          control={
            <Checkbox
              checked={aceitesLegais.aceiteLicencaDistribuicao}
              onChange={handleChangeAceitesLegais}
              name="aceiteLicencaDistribuicao"
              color="primary"
            />
          }
          label={
            <Typography variant="body2">
              <strong>Licença de Permissão de Uso:</strong> Concedo à (NOME DA UNIVERSIDADE) permissão não-exclusiva para arquivar, manter e tornar acessível, na plataforma institucional, a minha tese ou dissertação, no todo ou em parte, em suporte digital. Autorizo a (NOME DA UNIVERSIDADE) a arquivar e, sem alterar o conteúdo, converter a tese ou dissertação entregue, para qualquer formato de ficheiro, meio ou suporte, nomeadamente através da sua digitalização, para efeitos de preservação e acesso.
            </Typography>
          }
        />
      </Paper>
      
      <Alert severity="warning">
        <Typography variant="body2">
          Ao clicar em &quot;Arquivar Monografia&quot;, você confirma que leu e concorda com os termos acima e que as informações fornecidas são verdadeiras.
        </Typography>
      </Alert>
    </Box>
  );
  
  // Renderizar a etapa atual
  const renderEtapaAtual = () => {
    switch (etapaAtual) {
      case 0: return renderEtapa1();
      case 1: return renderEtapa2();
      case 2: return renderEtapa3();
      default: return null;
    }
  };

  return (
    <>
      <Button 
        variant="contained" 
        color="primary" 
        startIcon={<StorageIcon />} 
        onClick={handleOpen}
        disabled={monografia.status !== 'APROVADA'}
      >
        Arquivar no Repositório
      </Button>

      <Dialog 
        open={dialogOpen} 
        onClose={handleClose} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          Arquivar Monografia no Repositório Digital
        </DialogTitle>
        
        <DialogContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : success ? (
            <Alert severity="success" sx={{ my: 2 }}>
              Monografia arquivada com sucesso no repositório digital!
            </Alert>
          ) : (
            <Box sx={{ my: 2 }}>
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}
              
              <Stepper activeStep={etapaAtual} sx={{ mb: 4 }}>
                <Step>
                  <StepLabel>Informações Básicas</StepLabel>
                </Step>
                <Step>
                  <StepLabel>Dados do Autor</StepLabel>
                </Step>
                <Step>
                  <StepLabel>Direitos e Permissões</StepLabel>
                </Step>
              </Stepper>
              
              {renderEtapaAtual()}
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          {!success && (
            <>
              <Button onClick={handleClose}>
                Cancelar
              </Button>
              
              {etapaAtual > 0 && (
                <Button 
                  onClick={voltarEtapa}
                  startIcon={<BackIcon />}
                >
                  Voltar
                </Button>
              )}
              
              {etapaAtual < totalEtapas - 1 ? (
                <Button 
                  onClick={avancarEtapa}
                  variant="contained" 
                  color="primary"
                  endIcon={<NextIcon />}
                  disabled={!etapaValida()}
                >
                  Próximo
                </Button>
              ) : (
                <Button 
                  onClick={handleSubmit} 
                  variant="contained" 
                  color="primary"
                  disabled={!etapaValida() || loading}
                >
                  Arquivar Monografia
                </Button>
              )}
            </>
          )}
          
          {success && (
            <Button 
              onClick={handleClose} 
              variant="contained" 
              color="primary"
            >
              Fechar
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

ArquivarMonografia.propTypes = {
  monografia: PropTypes.object.isRequired,
  onSuccess: PropTypes.func
};

export default ArquivarMonografia;